/*
 * Copyright (c) 2024 NOOGA DEVELOPMENT, All rights reserved.
 * Author Julia Romanovskaya <julia.romanovskaya@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import { switchMap, map, catchError, takeUntil } from 'rxjs/operators';
import { documentsClient } from '../../../api/documents/documentsClient';
import { EMPTY, from, interval, of } from 'rxjs';
import {
    CashBoxDocumentsTabAction,
    CashBoxDocumentsTabActionType,
    fetchCashBoxDocumentsSuccessAction,
    checkNewDocumentsSuccessAction,
    downloadDocumentsSuccessAction,
    downloadDocumentsErrorAction,
    fetchCashBoxDocumentsErrorAction,
} from '../../../actions/CashBoxRegistry/Tabs/cashBoxDocumentsTabActions';
import { parseError, DATE_TIME_WITH_MS_FORMAT } from '../../../../utils/utils';
import { i18n } from '../../../translation/i18n';
import { AxiosError } from 'axios';
import moment from 'moment';

export const fetchCashBoxDocumentsEpic: Epic<CashBoxDocumentsTabAction> = (action$, state$) => {
    return action$.pipe(
        ofType(
            CashBoxDocumentsTabActionType.OPEN_CASHBOX_DOCUMENTS_TAB,
            CashBoxDocumentsTabActionType.APPLY_DOCUMENTS_PAGING,
            CashBoxDocumentsTabActionType.APPLY_DOCUMENTS_FILTERING,
        ),
        switchMap((action) => {
            const state = state$.value.cashBoxDocumentsTabReducer;
            const { taxNumber } = action.payload;

            return from(
                documentsClient.listDocuments({
                    ...state.docsFilterParams,
                    search_text: taxNumber,
                    limit: state.docsLimit,
                    afterKeyCreatedAtUtc: state.docsAfterKeyCreatedAtUtc,
                    afterKeyId: state.docsAfterKeyId,
                }),
            ).pipe(
                map((resp) => {
                    return fetchCashBoxDocumentsSuccessAction(taxNumber, resp);
                }),
                catchError((err: AxiosError) =>
                    of(
                        fetchCashBoxDocumentsErrorAction(
                            parseError(err, i18n.t('RegistryScreens.errors.documentsRequestFailed')),
                        ),
                    ),
                ),

                takeUntil(action$.pipe(ofType(CashBoxDocumentsTabActionType.EXIT_CASHBOX_DOCUMENTS_TAB))),
            );
        }),
    );
};

const POLLING_DOCS_INTERVAL = 15000;

export const fetchDocumentsByIntervalEpic: Epic<CashBoxDocumentsTabAction> = (action$, state$) => {
    return action$.pipe(
        ofType(CashBoxDocumentsTabActionType.FETCH_CASHBOX_DOCUMENTS_SUCCESS),
        switchMap((action) => {
            const { taxNumber } = action.payload;
            const state = state$.value.cashBoxDocumentsTabReducer;
            if (!state.docsFilterParams.dateTo && !moment(state.docsFilterParams.dateFrom).isAfter(moment())) {
                return interval(POLLING_DOCS_INTERVAL).pipe(
                    switchMap(() => {
                        const { newDocsInfo } = state$.value.cashBoxDocumentsTabReducer;

                        const dateFrom = moment(newDocsInfo.afterDate)
                            .add(1, 'milliseconds')
                            .utc()
                            .format(DATE_TIME_WITH_MS_FORMAT);
                        const dateTo = moment().add(1, 'days').utc().format(DATE_TIME_WITH_MS_FORMAT);

                        const searchParams = {
                            ...state.docsFilterParams,
                            search_text: taxNumber,
                            limit: 10000,
                            dateFrom,
                            dateTo,
                        };

                        return documentsClient.listDocuments(searchParams);
                    }),
                    map((list) => {
                        return checkNewDocumentsSuccessAction(list);
                    }),
                    catchError((err: AxiosError) =>
                        of(
                            fetchCashBoxDocumentsErrorAction(
                                parseError(err, i18n.t('RegistryScreens.errors.documentsRequestFailed')),
                            ),
                        ),
                    ),

                    takeUntil(action$.pipe(ofType(CashBoxDocumentsTabActionType.EXIT_CASHBOX_DOCUMENTS_TAB))),
                );
            } else {
                return EMPTY;
            }
        }),
    );
};

export const cashBoxDocsDownloadEpic: Epic<CashBoxDocumentsTabAction> = (actions$, state$) => {
    return actions$.pipe(
        ofType(CashBoxDocumentsTabActionType.DOWNLOAD_DOCUMENTS),
        switchMap((action) => {
            const { taxNumber } = action.payload;
            const state = state$.value.cashBoxDocumentsTabReducer;
            const searchParams = {
                ...state.docsFilterParams,
                search_text: taxNumber,
            };
            return from(documentsClient.downloadDocumentsAsExcel(searchParams)).pipe(
                map(() => downloadDocumentsSuccessAction()),

                catchError((err: AxiosError) =>
                    of(
                        downloadDocumentsErrorAction(
                            parseError(err, i18n.t('downloadings.errors.requestToCreateFileFailed')),
                        ),
                    ),
                ),
                takeUntil(actions$.pipe(ofType(CashBoxDocumentsTabActionType.EXIT_CASHBOX_DOCUMENTS_TAB))),
            );
        }),
    );
};
